<template>
  <div v-if="errorResponse">
    <WarningPanel
      :title="'There\'s a problem...'"
      :msg="errorResponse"
      :severe="true"
    />
  </div>
  <div v-else class="page-wrapper">
    <div
      class="agent-message agent-summary"
      v-if="agent.isValid"
      v-html="agent.message"
    />

    <div class="package-summary-header" :class="{ 'has-agent': agent.isValid }">
      <div class="your-package" data-testid="page-title">Your Package</div>
      <div id="price-discount-section" v-show="!isDiscountValidating">
        <section class="price-display">
          <div v-if="discountApplied" class="nondiscounted-price">
            £{{ nonDiscountedPricePerPersonPerWeek }}
          </div>
          <div :class="discountApplied ? 'price__discount-applied' : 'price'">
            £{{ pricePerPersonPerWeek }}
          </div>
        </section>
        <div
          class="pppw"
          :class="{
            'mb-30': !shouldShowVouchers,
          }"
        >
          <span>per person per week. Based on a 12 month contract.</span>
          <monthly-price-info />
        </div>
        <discount-activation
          v-if="shouldShowVouchers"
          v-model.trim="discountCode"
          @apply="applyDiscountCode"
          @clear="clearDiscountCode"
          :summary-control="true"
        />
      </div>
      <div
        id="price-discount-section"
        style="padding-top: 8rem; padding-bottom: 8rem;"
        v-if="isDiscountValidating"
      >
        <i
          class="fa-solid fa-spinner-third fa-spin fa-2x"
          data-testid="voucher-loading-animation"
        ></i>
      </div>
      <div class="tagline">No fees. No deposits.<br />One payment a month.</div>
    </div>

    <button
      class="btn btn--big --main-cta"
      data-testid="summary-next-button"
      @click="join"
    >
      Next
    </button>

    <check-list :items="ctaItems" />

    <hr />

    <PackageSummary
      class="package-summary"
      :inline-editing="agent.isValid"
      :hide-unwanted-products="!agent.isValid"
      thin
    >
      <template #summary-header>
        <h2 class="summary-header" data-testid="package-summary-header">
          Package for <strong>{{ housemateCount }}</strong>
          <template v-if="housemateCount === 1">
            housemate
          </template>
          <template v-else>
            housemates
          </template>
          at
          <strong class="address-or-postcode">{{ addressOrPostcode }}</strong>
        </h2>
        <button
          class="edit-pkg-button unstyled-button"
          @click="editPackage"
          data-testid="edit-package"
        >
          Edit package?
        </button>
      </template>
    </PackageSummary>

    <div class="save-action">
      <button
        class="unstyled-button --save-cta"
        @click="showSaveShareModal"
        data-testid="package-share-button"
      >
        Save or share package
      </button>
    </div>

    <hr />

    <div class="summary-footer">
      <button class="btn btn--big --main-cta" @click="join">
        Next
      </button>
    </div>

    <PackageSaveShareOverlay
      v-if="saveShareOverlayVisible"
      @close-panel="toggleSaveSharePanel"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import PackageSummary from "@/components/PackageSummary";
import PackageSaveShareOverlay from "@/components/PackageSaveShareOverlay";
import WarningPanel from "@/components/WarningPanel";
import CheckList from "@/components/CheckList";
import DiscountActivation from "@/components/DiscountActivation.vue";
import {
  initialDiscountState,
  initialPackagePrice,
} from "@/store/initialState";
import MonthlyPriceInfo from "@/components/MonthlyPriceInfo.vue";
import ShortSummaryControl from "@/components/ShortSummaryControl.vue";

export default {
  name: "ShortSummaryControl",
  data() {
    return {
      saveShareOverlayVisible: false,
      errorResponse: "",
      ctaItems: [
        "Bundle all of your utilities into one payment",
        "Everyone pays their fair share",
        "No deposit or set up fees",
      ],
      pricePerPersonPerWeekFormatted: (
        this.$store.getters.pricePerPerson / 100
      ).toFixed(2),
      nonDiscountedPricePerPersonPerWeekFormatted: (
        this.$store.getters.nonDiscountedPricePerPerson / 100
      ).toFixed(2),
    };
  },
  computed: {
    ...mapState(["agent", "discount"]),
    ...mapGetters({
      housemateCount: "housemateCount",
      postcode: "postcode",
      hasUsedDiscount: "hasUsedDiscount",
      hasAddress: "hasAddress",
      addressLine1: "addressLine1",
      address: "getCustomerAddressString",
      shouldShowVouchers: "shouldShowVouchers",
    }),
    nonDiscountedPricePerPersonPerWeek() {
      return this.nonDiscountedPricePerPersonPerWeekFormatted;
    },
    pricePerPersonPerWeek() {
      return this.pricePerPersonPerWeekFormatted;
    },
    addressOrPostcode() {
      if (this.hasAddress) {
        return this.address;
      } else {
        return this.postcode;
      }
    },
    discountCode: {
      get() {
        return this.$store.state.discount.code;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { code: value });
      },
    },
    discountApplied: {
      get() {
        return this.$store.state.discount.isValid;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValid: value });
      },
    },
    isDiscountValidating: {
      get() {
        return this.$store.state.discount.isValidating;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValidating: value });
      },
    },
  },
  methods: {
    ...mapActions([
      "getProducts",
      "getReferralMessage",
      "getAffiliateMessage",
      "activateDiscount",
    ]),
    ...mapMutations({
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      setProductOption: "SET_PRODUCT_OPTION",
      setPostcode: "SET_POSTCODE",
      setHousemateCount: "SET_HOUSEMATE_COUNT",
      setProducts: "SET_PRODUCTS",
      setPackagePrice: "SET_PACKAGE_PRICE",
      setIsViaShareLink: "IS_VIA_SHARELINK",
      hideBackButton: "HIDE_BACK_BTN",
      setDiscountState: "SET_DISCOUNT_STATE",
      setLeadTenantAddress: "SET_LEAD_TENANT_ADDRESS",
    }),
    showSaveShareModal() {
      this.trackGoogleEvent("Click Share", "Get Price", "Share price");
      this.trackFacebookEvent("Click Share");
      this.toggleSaveSharePanel();
    },
    toggleSaveSharePanel() {
      this.saveShareOverlayVisible = !this.saveShareOverlayVisible;
    },
    join() {
      this.trackGoogleEvent(
        "Click Create Account",
        "Create Account",
        "Create Account",
      );
      this.trackFacebookEvent("Click Create Account");

      if (this.hasAddress) {
        this.$router.push("/your-details");
      } else {
        this.$router.push("/your-address");
      }
    },
    editPackage() {
      this.$router.push(`/products/${this.$store.state.products[0].type}`);
    },
    applyDiscountCode() {
      this.$set(this, "isDiscountValidating", true);

      this.activateDiscount({
        voucherCode: this.$store.state.discount.code,
        selectedProducts: this.$store.getters.selectedProductPriceIds,
        housemateCount: this.$store.state.housemateCount,
        leadId: this.$store.state.leadId,
      })
        .then(({ data }) => {
          this.$set(this, "discountApplied", true);

          this.$store.commit("SET_PACKAGE_PRICE", {
            fullPriceAmount: data.priceBeforeDiscount,
            discountedPriceAmount: data.priceAfterDiscount,
          });

          this.$store.commit("SET_DISCOUNT_STATE", {
            description: data.message,
            errorMsg: "",
          });

          this.pricePerPersonPerWeekFormatted = (
            data.priceAfterDiscount / 100
          ).toFixed(2);
          this.nonDiscountedPricePerPersonPerWeekFormatted = (
            data.priceBeforeDiscount / 100
          ).toFixed(2);

          this.trackGoogleEvent(
            "Voucher applied successfully",
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .catch((err) => {
          this.$store.commit("SET_DISCOUNT_STATE", {
            errorMsg:
              err.response.data.message ||
              "Invalid voucher code. Check and try again.",
          });

          this.trackGoogleEvent(
            `Voucher applied failed`,
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .finally(() => {
          this.$set(this, "isDiscountValidating", false);
        });
    },
    clearDiscountCode() {
      this.$store.commit("SET_PACKAGE_PRICE", initialPackagePrice());
      this.$store.commit("SET_DISCOUNT_STATE", initialDiscountState());

      this.pricePerPersonPerWeekFormatted = (
        this.$store.getters.pricePerPerson / 100
      ).toFixed(2);
      this.nonDiscountedPricePerPersonPerWeekFormatted = (
        this.$store.getters.nonDiscountedPricePerPerson / 100
      ).toFixed(2);
    },
    loadShareLinkData() {
      const addressGuid = this.$route.query.address;

      this.getProducts({
        addressGuid,
        postcode: this.$route.query.postcode,
        housemateCount: this.$route.query.housemates,
        voucherCode: this.$route.query.voucherCode,
        hash: this.$route.query.hash,
      })
        .then(({ data }) => {
          // ensure errorResponse is reset
          this.errorResponse = "";

          if (data.address) {
            this.setLeadTenantAddress(data.address);
            this.setPostcode(data.address.postcode);
          }

          if (data.existingAccountGUID) {
            this.$router.push({
              name: "account-already-exists",
              params: { guid: data.existingAccountGUID },
            });
          } else {
            // set the store state
            if (this.$route.query.postcode) {
              this.setPostcode(this.$route.query.postcode);
            }
            this.setHousemateCount(this.$route.query.housemates);
            this.setProducts(data.products);

            if (data.priceBeforeDiscount) {
              this.nonDiscountedPricePerPersonPerWeekFormatted = (
                data.priceBeforeDiscount / 100
              ).toFixed(2);
            }

            if (data.priceAfterDiscount) {
              this.pricePerPersonPerWeekFormatted = (
                data.priceAfterDiscount / 100
              ).toFixed(2);
            }
            // Because the app has been entered mid-journey, we need to set a variable
            // that controls what the "back/editProduct" button directs to
            this.setIsViaShareLink(true);

            // Filter the query parameters ensuring the selected products are stored in state
            Object.keys(this.$route.query).forEach((key) => {
              // This mutation performs a find on the products array based on the
              // query key, so it's fine to pass the whole query parameter set
              this.setProductOption({
                type: key,
                code: this.$route.query[key],
              });
            });

            // If referral code, commit it to the store...
            if (this.$route.query.ref) {
              this.getReferralMessage(this.$route.query.ref);
            }

            // If affiliate code, commit it to the store...
            if (this.$route.query.aff) {
              this.getAffiliateMessage(this.$route.query.aff);
            }

            if (this.$route.query.voucherCode) {
              this.setDiscountState({
                inputRevealed: true,
                code: this.$route.query.voucherCode,
              });
              this.applyDiscountCode();
            }

            if (Object.hasOwn(data, "discount")) {
              this.$store.commit("SET_DISCOUNT_STATE", {
                isValid: data.discount.isValid,
                inputRevealed: data.discount.inputRevealed,
                offer: data.discount.offer,
                aff: data.discount.aff,
                code: data.discount.code,
              });

              if (Object.hasOwn(data, "leadId")) {
                this.$store.commit("SET_LEAD_ID", data.leadId);
              }
            }
          }
        })
        .catch((error) => {
          if (!error.response) {
            this.errorResponse =
              "Network error accessing the Split The Bills API";
          } else {
            this.errorResponse = error.response.data.messages[0];
          }
        })
        .finally(() => {
          this.hideLoadingPanel();
        });
    },
  },
  created() {
    this.$store.commit("HIDE_BACK_BTN");
    this.clearDiscountCode();

    // Check if this is a share link (has required query parameters)
    const hasAddressOrPostcode =
      this.$route.query.address || this.$route.query.postcode;
    const hasHousemates = this.$route.query.housemates;

    // Only run share link logic if we have the required parameters
    if (hasAddressOrPostcode && hasHousemates) {
      this.loadShareLinkData();
    } else {
      this.hideLoadingPanel();
    }
  },
  components: {
    ShortSummaryControl,
    MonthlyPriceInfo,
    DiscountActivation,
    CheckList,
    PackageSaveShareOverlay,
    PackageSummary,
    WarningPanel,
  },
};
</script>

<style lang="scss" scoped>
.package-summary {
  background: $white;
  text-align: center;
  padding-bottom: $spacing-medium;

  .edit-pkg-button {
    margin: 2rem 0;
    text-decoration: underline;
    font-size: 20px;
    color: $brand-green;
  }
}

.package-summary-header {
  @extend h1;
  margin: 0 0 $spacing-medium;

  .your-package {
    margin-bottom: $spacing-xs;
  }

  .price-display {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.3em;
    margin-bottom: 0.5em;
  }

  .nondiscounted-price {
    color: $brand-orange;
    text-decoration: line-through;
    font-size: $form-font-size;
    font-weight: $font-medium;
  }

  .price {
    font-size: 34px;
    font-weight: $font-medium;

    &__discount-applied {
      color: $brand-green;
    }
  }

  .nondiscounted-price + .price {
    line-height: 0.9;
    margin-bottom: 4px;
  }

  .pppw,
  .tagline {
    color: $text;
    font-weight: 300;
  }

  .pppw {
    font-size: 16px;
    margin-bottom: 0.3rem;
  }

  .tagline {
    margin-top: 1rem;
    font-size: 20px;
    font-weight: $font-regular;
  }

  &.has-agent {
    padding-top: $spacing-xs;
  }
}

.--main-cta {
  @include media("<medium") {
    margin-bottom: $spacing-small;
  }
}

.save-action {
  text-align: center;

  .--save-cta {
    color: $brand-green;
    margin: 0;

    font-weight: $font-bold;
    text-decoration: underline;
  }
}

.summary-header {
  text-align: center;
  margin-bottom: 0;
  font-size: 18px;
  font-weight: $font-regular;

  .address-or-postcode {
    color: $brand-orange;
  }
}

.summary-footer {
  padding: $spacing-small 0 0 0;
  text-align: center;
}
</style>

<style lang="scss">
.agent-summary {
  padding-top: $spacing-small;

  @include media(">=medium") {
    margin: 0 $spacing-medium;
  }
}

.mb-30 {
  margin-bottom: 30px;
}

.pppw span {
  display: block;
  margin-bottom: 4px;
}

.pppw span:last-child {
  margin-bottom: 0; /* Remove gap after the last line */
}
</style>
